// @eidollon/contracts/filters
//
// PLATFORM FILTER CONTRACT — locked Day 0 of Wave 4.
//
// Per CLAUDE.md Iron Rule 11, every entity-browser surface in Eidollon
// (Horizon, Terminal panes, Intel, Ops, Audit, Vendor browser, Agency
// browser, Award browser) consumes the same `FilterSchema` shape and the
// same `FilterAst` query language. Compilers in `services/api` translate
// `FilterAst` to SQL today and OpenSearch DSL tomorrow.
//
// Two representations:
//
//   * `FilterState` — flat `{ field_id: value }` map. URL-friendly. Covers
//     the majority of cases (rail + chip-strip modes A/B). Always
//     interpreted as conjunctive (AND across fields, OR within multi-enum).
//
//   * `FilterAst`   — explicit AND/OR/NOT tree. Lives behind the advanced
//     QueryBuilder (mode C) and behind the NL → AST compiler. Supersedes
//     `FilterState` when both are present in a request.
//
// Both round-trip into URL search params via the canonical encoder in
// `packages/ui-filters/src/url.ts`.

import { z } from 'zod';

/* ──────────────────────────────────────────────────────────────────────────
 *  Field types — what shape of value each facet accepts
 * ────────────────────────────────────────────────────────────────────────── */

export const FilterFieldType = z.enum([
  'text', // free-text contains
  'enum', // single-select
  'multi-enum', // multi-select; OR within field
  'date-range', // { from?: ISO, to?: ISO }
  'money-range', // { min?: number, max?: number, currency?: ISO 4217 }
  'number-range', // { min?: number, max?: number }
  'bool', // tri-state: true | false | undefined (any)
  'async-enum', // multi-select with async typeahead
  'geo-polygon', // { polygons: [{ id, label?, polygon: GeoJSON.Polygon }] } — user-drawn AOIs
]);
export type FilterFieldType = z.infer<typeof FilterFieldType>;

export const FilterField = z
  .object({
    id: z.string().min(1).max(64),
    label: z.string().min(1).max(64),
    type: FilterFieldType,
    // Both flags are optional in user-facing config (default to true on the
    // wire); when present they explicitly opt out. We deliberately keep them
    // as `optional` so the inferred TS type doesn't force every consumer to
    // restate platform defaults.
    facetable: z.boolean().optional(), // shows in the rail FacetPanel (default true)
    queryable: z.boolean().optional(), // shows in the QueryBuilder (default true)
    // For async-enum, the typeahead source is a path like /v1/search/typeahead/agencies
    typeaheadPath: z.string().optional(),
    // Optional pinned options (e.g. "active", "expired") — UI shows these
    // before any async results come in.
    pinned: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
    description: z.string().max(240).optional(),
  })
  .strict();
export type FilterField = z.infer<typeof FilterField>;

export const SortSpec = z
  .object({
    field: z.string().min(1).max(64),
    dir: z.enum(['asc', 'desc']).default('desc'),
  })
  .strict();
export type SortSpec = z.infer<typeof SortSpec>;

export const FilterSchema = z
  .object({
    entity: z.string().min(1).max(32), // 'OPPORTUNITY' | 'AWARD' | 'VENDOR' | …
    fields: z.array(FilterField).max(64),
    defaultSort: SortSpec,
  })
  .strict();
export type FilterSchema = z.infer<typeof FilterSchema>;

/* ──────────────────────────────────────────────────────────────────────────
 *  FilterValue — the per-field value union
 * ────────────────────────────────────────────────────────────────────────── */

const ISO = z.string().min(8); // 'YYYY-MM-DD' or full ISO
const Iso2 = z.string().length(2);
const Money = z
  .object({
    min: z.number().nonnegative().nullable().optional(),
    max: z.number().nonnegative().nullable().optional(),
    currency: Iso2.optional(),
  })
  .strict();
const NumRange = z
  .object({
    min: z.number().nullable().optional(),
    max: z.number().nullable().optional(),
  })
  .strict();
const DateRange = z
  .object({
    from: ISO.nullable().optional(),
    to: ISO.nullable().optional(),
  })
  .strict();

// GeoJSON Polygon ring: an array of [lng, lat] (or [lng, lat, alt]) coordinate
// pairs. RFC 7946 says the first and last positions of each linear ring MUST
// be identical; we don't enforce that here (the consumer normalizes), but we
// require at least 4 positions per ring so a ring is well-formed.
//
// SECURITY (CRIT-A): the `geo-polygon` value lands in PostGIS via
// `ST_GeomFromGeoJSON` inside the saved_screen_aois sync trigger. An
// attacker-controlled polygon with millions of vertices could pin a worker
// indefinitely. Bounds enforced here at the wire boundary so the trigger
// never sees a pathological shape:
//   * MAX_RINGS_PER_POLYGON = 10  (1 outer + 9 inner holes is more than any
//     real AOI needs)
//   * MAX_VERTICES_PER_RING = 1000 (covers the most baroque coastlines a
//     user can hand-draw; a real KML import beyond this should be Douglas-
//     Peucker simplified before save)
//   * MAX_AOIS_PER_TENANT = 25 (founder cap; matches the matcher's 95p
//     <200ms budget at 25 active AOIs per tenant)
export const MAX_RINGS_PER_POLYGON = 10;
export const MAX_VERTICES_PER_RING = 1000;
export const MAX_AOIS_PER_TENANT = 25;

const GeoPosition = z.tuple([z.number(), z.number()]).rest(z.number());
const GeoPolygonGeometry = z
  .object({
    type: z.literal('Polygon'),
    coordinates: z
      .array(z.array(GeoPosition).min(4).max(MAX_VERTICES_PER_RING))
      .min(1)
      .max(MAX_RINGS_PER_POLYGON),
  })
  .strict();
const GeoPolygonEntry = z
  .object({
    id: z.string().min(1).max(64),
    label: z.string().max(120).optional(),
    polygon: GeoPolygonGeometry,
  })
  .strict();
export const GeoPolygonValue = z
  .object({
    polygons: z.array(GeoPolygonEntry).max(MAX_AOIS_PER_TENANT),
  })
  .strict();
export type GeoPolygonValue = z.infer<typeof GeoPolygonValue>;

export const FilterValue = z.union([
  z.string(), // text / enum
  z
    .array(z.string())
    .max(256), // multi-enum / async-enum
  z.boolean(), // bool
  DateRange,
  Money,
  NumRange,
  GeoPolygonValue, // geo-polygon
]);
export type FilterValue = z.infer<typeof FilterValue>;

/* ──────────────────────────────────────────────────────────────────────────
 *  FilterState — flat map. The URL-canonical form for the majority of users.
 * ────────────────────────────────────────────────────────────────────────── */

export const FilterState = z.record(z.string(), FilterValue);
export type FilterState = z.infer<typeof FilterState>;

/* ──────────────────────────────────────────────────────────────────────────
 *  FilterAst — explicit AND/OR/NOT tree for the QueryBuilder + NL compiler
 *
 *  The compiler in services/api whitelists field ids per entity and emits
 *  parameterized SQL only. Field names from the AST are looked up in the
 *  whitelist; values flow through $1, $2, … placeholders. Never concatenate.
 * ────────────────────────────────────────────────────────────────────────── */

export const ComparisonOp = z.enum([
  'eq',
  'ne',
  'in',
  'nin',
  'between',
  'gt',
  'gte',
  'lt',
  'lte',
  'like', // case-insensitive substring (Postgres ILIKE / OpenSearch wildcard)
  'exists', // field IS NOT NULL
  'missing', // field IS NULL
]);
export type ComparisonOp = z.infer<typeof ComparisonOp>;

// Recursive AST. Defined via z.lazy + an explicit TS type alias so the
// inferred type is usable across the codebase.
export type FilterAst =
  | { op: 'and'; children: FilterAst[] }
  | { op: 'or'; children: FilterAst[] }
  | { op: 'not'; child: FilterAst }
  | { op: ComparisonOp; field: string; value?: unknown };

export const FilterAst: z.ZodType<FilterAst> = z.lazy(() =>
  z.discriminatedUnion('op', [
    z.object({ op: z.literal('and'), children: z.array(FilterAst).min(1).max(32) }).strict(),
    z.object({ op: z.literal('or'), children: z.array(FilterAst).min(1).max(32) }).strict(),
    z.object({ op: z.literal('not'), child: FilterAst }).strict(),
    z
      .object({ op: ComparisonOp, field: z.string().min(1).max(64), value: z.unknown().optional() })
      .strict(),
  ]),
);

/* ──────────────────────────────────────────────────────────────────────────
 *  Per-entity FilterSchemas
 *
 *  These live alongside the platform contract so every consumer (rail,
 *  IntentBar, query compiler, NL → AST template, audit) reads the same
 *  source of truth. Add a new entity by appending another schema export.
 * ────────────────────────────────────────────────────────────────────────── */

/** Intel page — Live Intelligence Layer (sanctions, conflict, supply,
 *  obligations, policy, aviation, seismic, weather, anomalies). */
export const intelFilterSchema: FilterSchema = {
  entity: 'INTEL_SIGNAL',
  defaultSort: { field: 'occurred_at', dir: 'desc' },
  fields: [
    {
      // RESCOPED (T2): the platform field is now the canonical sub-category
      // key from `@eidollon/contracts/intel-domains` (e.g. `civil_unrest`,
      // `earthquake`, `oil_tanker`). The rail surface renders these via the
      // HierarchicalFilter accordion, not a flat multi-enum facet, so we
      // opt out of FacetPanel rendering (`facetable: false`) and host the
      // picker through EntityScreen's `railTopSlot`.
      id: 'signal_type',
      label: 'TYPE',
      type: 'multi-enum',
      facetable: false,
      pinned: [
        // conflict
        { value: 'civil_unrest', label: 'civil_unrest' },
        { value: 'armed_conflict', label: 'armed_conflict' },
        { value: 'terrorism', label: 'terrorism' },
        { value: 'military_activity', label: 'military_activity' },
        { value: 'infrastructure_attack', label: 'infrastructure_attack' },
        { value: 'sanctions_export', label: 'sanctions_export' },
        { value: 'cyber_activity', label: 'cyber_activity' },
        // disaster
        { value: 'earthquake', label: 'earthquake' },
        { value: 'flooding', label: 'flooding' },
        { value: 'wildfire', label: 'wildfire' },
        { value: 'storm', label: 'storm' },
        { value: 'disease_outbreak', label: 'disease_outbreak' },
        { value: 'humanitarian_crisis', label: 'humanitarian_crisis' },
        // trade
        { value: 'oil_tanker', label: 'oil_tanker' },
        { value: 'commercial_shipping', label: 'commercial_shipping' },
        { value: 'port_congestion', label: 'port_congestion' },
        { value: 'supply_chain_disruption', label: 'supply_chain_disruption' },
      ],
    },
    {
      id: 'region',
      label: 'REGION',
      type: 'async-enum',
      typeaheadPath: '/v1/intel/typeahead/regions',
    },
    {
      // Map drill-down only: set by clicking a choropleth region on the Intel
      // map (writes `regions.id`). NOT a rail facet and NOT a QueryBuilder
      // field — it carries an opaque uuid, not a user-typed value — but it IS
      // listed here so the IntentBar renders a removable chip for it (the
      // chip strip skips any active filter key absent from the schema, which
      // would otherwise strand a map-set region filter with no way to clear
      // it from the bar). Server-side it compiles to a spatial intersect
      // against the region's geometry (see INTEL_SCHEMA.region_id in
      // services/api filter-compiler).
      id: 'region_id',
      label: 'REGION',
      type: 'text',
      facetable: false,
      queryable: false,
    },
    {
      id: 'severity',
      label: 'SEVERITY',
      type: 'number-range',
    },
    {
      id: 'time_window',
      label: 'TIME',
      type: 'date-range',
    },
    {
      id: 'source',
      label: 'SOURCE',
      type: 'multi-enum',
      pinned: [
        { value: 'OFAC', label: 'OFAC' },
        { value: 'EU', label: 'EU' },
        { value: 'UN', label: 'UN' },
        { value: 'ACLED', label: 'ACLED' },
        { value: 'GDELT', label: 'GDELT' },
        { value: 'AIS', label: 'AIS' },
        { value: 'USGS', label: 'USGS' },
        { value: 'NOAA', label: 'NOAA' },
        { value: 'internal', label: 'internal' },
      ],
    },
    {
      id: 'linked_to_opportunity',
      label: 'LINKED OPP',
      type: 'bool',
      description: 'Only signals with at least one linked opportunity',
    },
    {
      id: 'agency',
      label: 'AGENCY',
      type: 'async-enum',
      typeaheadPath: '/v1/intel/typeahead/agencies',
    },
    {
      id: 'naics',
      label: 'NAICS',
      type: 'async-enum',
      typeaheadPath: '/v1/intel/typeahead/naics',
    },
    // ─── Conflict-specific advanced facets ────────────────────────────
    // These pull from the ACLED + GDELT payload shape we land into
    // intelligence_signals.payload jsonb. The filter-compiler has the
    // matching whitelist + jsonb-path SQL.
    {
      id: 'event_type',
      label: 'EVENT TYPE',
      type: 'multi-enum',
      pinned: [
        { value: 'battle', label: 'battle' },
        { value: 'explosion', label: 'explosion / IED' },
        { value: 'violence', label: 'violence vs civilians' },
        { value: 'riot', label: 'riot' },
        { value: 'protest', label: 'protest' },
        { value: 'strategic_dev', label: 'strategic development' },
      ],
      description: 'ACLED event-type classification',
    },
    {
      id: 'actor',
      label: 'ACTOR',
      type: 'async-enum',
      typeaheadPath: '/v1/intel/typeahead/actors',
      description: 'Named state/non-state actor (ACLED actor1/actor2)',
    },
    {
      id: 'fatalities',
      label: 'FATALITIES',
      type: 'number-range',
      description: 'Reported fatalities count for the event',
    },
    // ─── Sanctions-specific advanced facets ──────────────────────────
    {
      id: 'sanction_list',
      label: 'SANCTION LIST',
      type: 'multi-enum',
      pinned: [
        { value: 'OFAC_SDN', label: 'OFAC SDN' },
        { value: 'OFAC_NS', label: 'OFAC Non-SDN' },
        { value: 'EU_FSF', label: 'EU FSF' },
        { value: 'UN_CONS', label: 'UN consolidated' },
        { value: 'UK_HMT', label: 'UK HMT' },
      ],
    },
    {
      id: 'sanction_program',
      label: 'SAN PROGRAM',
      type: 'multi-enum',
      pinned: [
        { value: 'UKRAINE-EO13662', label: 'Ukraine (EO 13662)' },
        { value: 'RUSSIA-EO14024', label: 'Russia (EO 14024)' },
        { value: 'IRAN', label: 'Iran' },
        { value: 'DPRK', label: 'DPRK' },
        { value: 'SDGT', label: 'Specially Designated Global Terrorist' },
        { value: 'CYBER', label: 'Cyber-related' },
        { value: 'NARC', label: 'Narcotics' },
      ],
    },
    // ─── Severity bucket (derived: HOT ≥90 / HIGH 70-89 / MED 40-69 / LOW <40) ─
    // Boundaries live in @eidollon/contracts/intel.SEVERITY_BUCKETS so the
    // chip, the client `applyFilter`, and the server `bucket` SQL builder
    // never drift. Aligned to the W1-A heat ramp (`colors.ts#severityRamp`).
    {
      id: 'severity_bucket',
      label: 'SEV BUCKET',
      type: 'multi-enum',
      pinned: [
        { value: 'HOT', label: 'HOT · ≥90' },
        { value: 'HIGH', label: 'HIGH · 70-89' },
        { value: 'MED', label: 'MED · 40-69' },
        { value: 'LOW', label: 'LOW · <40' },
      ],
    },
    {
      id: 'aoi',
      label: 'AOI',
      type: 'geo-polygon',
      description: 'Area of interest — user-drawn polygon(s)',
    },
  ],
};
